import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';

import { EditOutlined, Search } from '@mui/icons-material';
import {
  Box,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import CustomButton from 'components/Button';
import { FilterPanelContainer, PageContainer } from 'components/GlobalStyles/styles';
import Loader from 'components/Loader';
import SnackBar, { MessageType } from 'components/SnackBar';
import {
  useCreateEmployeeMutation,
  useGetAllEmployeesQuery,
  useGetBusmanPayrollCategoriesQuery,
  useGetTeamsQuery,
  useLazyGetEmployeeDetailsQuery,
  useUpdateEmployeeDetailMutation,
} from 'services/busmanApi';
import { EmployeeMinResponse } from 'types/EmployeeTypes';
import { EmployeeFormValues } from 'utils/schema';
import { sortLists } from 'utils/sort';

import { RootState } from '../../store';

import EmployeeDrawer, { DrawerOption } from './components/EmployeeDrawer';
import { Employee, EmployeeListContainer, EmployeesList } from './styles';

const NAME_COLUMN_WIDTH = '30%';
const EMAIL_COLUMN_WIDTH = '35%';
const ROLE_COLUMN_WIDTH = '30%';
const EDIT_COLUMN_WIDTH = '5%';

const ROLE_MAPPING: Record<string, string> = {
  E: 'Employee',
  A: 'Admin',
  M: 'Manager',
  S: 'Supervisor',
};

// Fall back to the raw code so an unmapped role from the API still renders something.
const getRoleLabel = (role: string) => ROLE_MAPPING[role] || role;

const EmployeePage = () => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { user } = useSelector((state: RootState) => state.auth);
  const { activities } = useSelector((state: RootState) => state.worklogs);

  const [fetchEmployeeDetail] = useLazyGetEmployeeDetailsQuery();
  const { data: employeeList, isFetching } = useGetAllEmployeesQuery({});
  const { data: busmanPayrollCategories, isFetching: fetchingBusman } = useGetBusmanPayrollCategoriesQuery({});
  const { data: teams } = useGetTeamsQuery(user?.user_id, { skip: !user?.user_id });
  const [createEmployee, { isLoading: isCreatingEmployee }] = useCreateEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdatingEmployee }] = useUpdateEmployeeDetailMutation();

  const [inputText, setInputText] = React.useState('');
  const [employeeDrawer, setEmployeeDrawer] = React.useState(false);
  const [editingEmployee, setEditingEmployee] = React.useState<EmployeeFormValues | null>(null);
  const [message, setMessage] = React.useState<MessageType | null>(null);

  const teamOptions: DrawerOption[] = React.useMemo(
    () => (teams ?? []).map((team) => ({ id: team.team_id, label: team.team_name })),
    [teams],
  );

  //  Need to change the filtering type
  const activityOptions: DrawerOption[] = React.useMemo(
    () =>
      (activities ?? [])
        .filter((activity) => activity.type !== null)
        .map((activity) => ({ id: activity.activity_id, label: activity.activity_name })),
    [activities],
  );

  const entitlementOptions: DrawerOption[] = React.useMemo(
    () =>
      (activities ?? [])
        .filter((activity) => activity.type !== null)
        .map((activity) => ({ id: activity.activity_id, label: activity.activity_name })),
    [activities],
  );

  const handleAddEmployee = async (payload: EmployeeFormValues) => {
    // The form holds the enum member names; the payload carries their ordinals.

    try {
      await createEmployee(payload).unwrap();
      setMessage({ type: 'success', msg: `${payload.firstname} ${payload.lastname} added successfully.` });
      return true;
    } catch (error) {
      setMessage({ type: 'error', msg: typeof error === 'string' ? error : 'Failed to add employee.' });
      return false;
    }
  };

  const handleUpdateEmployee = async (payload: EmployeeFormValues) => {
    try {
      await updateEmployee(payload).unwrap();
      setMessage({ type: 'success', msg: `${payload.firstname} ${payload.lastname} updated successfully.` });
      return true;
    } catch (error) {
      setMessage({ type: 'error', msg: typeof error === 'string' ? error : 'Failed to update employee.' });
      return false;
    }
  };

  const handleEditEmployee = async (employee: EmployeeMinResponse) => {
    const { data: userDetail } = await fetchEmployeeDetail({ empID: employee.user_id, getAddress: true });
    // The list endpoint only returns base fields, so split full_name and default the rest.
    const [firstname, ...rest] = (employee.full_name || '').split(' ');

    setEditingEmployee({
      employee_id: employee.user_id,
      firstname: firstname || '',
      lastname: rest.join(' '),
      username: userDetail?.username || '',
      email: employee.email || '',
      user_role: employee.user_role || '',
      is_active: employee.is_active ?? true,
      team_id: null,
      activity_ids: [],
      employment_status: userDetail?.payroll_details?.employment_status || '',
      employment_category: userDetail?.payroll_details?.employment_category || '',
      address: userDetail?.address,
    });
    setEmployeeDrawer(true);
  };

  const filteredEmployees = sortLists([...(employeeList || [])], 'full_name').filter((employee) => {
    if (inputText === '') {
      return true;
    }

    return (
      employee?.full_name?.toLowerCase().includes(inputText.toLowerCase()) ||
      employee?.email?.toLowerCase().includes(inputText.toLowerCase())
    );
  });

  return (
    <>
      <Helmet>
        <title>Busmin</title>
        <meta name="Busmin" content="EEA-Employees" />
      </Helmet>
      <Loader open={isFetching || fetchingBusman || isCreatingEmployee || isUpdatingEmployee} />
      <PageContainer>
        {/* Side Panel */}
        <FilterPanelContainer>
          {!isMobileScreen && (
            <Typography gutterBottom sx={{ mb: theme.spacing(2) }} variant="h4">
              Employees
            </Typography>
          )}
          <Stack direction="column" gap={2}>
            <Box
              display="flex"
              gap={2}
              alignItems="center"
              flexWrap="wrap"
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
              }}>
              <OutlinedInput
                endAdornment={
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                }
                onChange={(event) => setInputText(event.target.value)}
                placeholder="Search Employee"
                sx={{
                  height: '32px',
                  flex: 1,
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: '100%', sm: '200px' },
                }}
                value={inputText}
              />
            </Box>

            <Box sx={{ my: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <CustomButton
                text="Add Employee"
                variant="contained"
                onClick={() => setEmployeeDrawer(true)}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}
              />
            </Box>
          </Stack>
        </FilterPanelContainer>

        {/* Employee Table */}
        <EmployeeListContainer>
          {isMobileScreen ? (
            // Rows switch to a stacked card layout below sm, so skip the table entirely
            <EmployeesList>
              {filteredEmployees.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {inputText ? 'No employees found matching your search.' : 'No employees available.'}
                  </Typography>
                </Box>
              ) : (
                filteredEmployees.map((employee) => (
                  <Employee key={employee.user_id}>
                    <Typography sx={{ wordBreak: 'break-word' }} variant="subtitle1">
                      {employee.full_name} - {getRoleLabel(employee.user_role)}
                    </Typography>
                    <Typography color="text.secondary" sx={{ wordBreak: 'break-all' }} variant="body2">
                      {employee.email}
                    </Typography>
                  </Employee>
                ))
              )}
            </EmployeesList>
          ) : (
            <TableContainer sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: NAME_COLUMN_WIDTH, color: 'text.disabled' }}>NAME</TableCell>
                    <TableCell sx={{ width: EMAIL_COLUMN_WIDTH, color: 'text.disabled' }}>EMAIL</TableCell>
                    <TableCell align="center" sx={{ width: ROLE_COLUMN_WIDTH, color: 'text.disabled' }}>
                      ROLE
                    </TableCell>
                    <TableCell sx={{ width: EDIT_COLUMN_WIDTH }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ p: 4, textAlign: 'center', borderBottom: 'none' }}>
                        <Typography variant="body1" color="text.secondary">
                          {inputText ? 'No employees found matching your search.' : 'No employees available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map((employee) => (
                      <TableRow hover key={employee.user_id} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                        <TableCell>
                          <Typography variant="subtitle1">{employee.full_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle1">{employee.email}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="subtitle1">{getRoleLabel(employee.user_role)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            aria-label="Edit employee"
                            size="small"
                            onClick={() => handleEditEmployee(employee)}>
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </EmployeeListContainer>
      </PageContainer>

      <EmployeeDrawer
        initialValues={editingEmployee}
        open={employeeDrawer}
        isEditing={!!editingEmployee}
        teamOptions={teamOptions}
        activityOptions={activityOptions}
        entitlementOptions={entitlementOptions}
        payrollOptions={busmanPayrollCategories || []}
        onClose={() => {
          setEmployeeDrawer(false);
          setEditingEmployee(null);
        }}
        onSubmit={editingEmployee ? handleUpdateEmployee : handleAddEmployee}
      />
      <SnackBar message={message} onClose={() => setMessage(null)} />
    </>
  );
};

export default EmployeePage;
